import { apiProblemText } from "../../api/api-error.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Settings"` — P11-08.
 *
 * Workspace name and time zone, the developer surface, who can do what, alert rules, and the danger
 * zone. AMENDED 2026-10-06 (plan-014 C3): the developer surface carries workspace and organization API
 * keys (scope, end date, networks, owner, rotation — `api-key-panel.tsx`), and "Who can do what" names
 * the seven workspace roles and the four organization roles from the capability module
 * (`permission-matrix.tsx`).
 *
 * **THERE IS NO SSO PANEL, AND THAT IS THE DECISION RATHER THAN AN OMISSION.** Product
 * decision Q2c puts enterprise SSO configuration in the operator console for the first
 * release: `sso.write` is held by **no customer role at all**, which the permission matrix
 * renders as an empty row. A panel that could read but never write would be worse than none,
 * and the customer-facing Security page is a defined follow-up. Nothing here invents it.
 *
 * AMENDED 2026-09-25 (customer-portal review #61, #62, #63, #64, #134):
 *
 *   · **The time zone shows the stored zone, or nothing.** The box used to fall back to "UTC"
 *     until the workspace arrived (and for good if its read failed), and it saved on every change
 *     and on blur — so a Greek business saw UTC and could overwrite Europe/Athens with it. The
 *     form now waits for the workspace, offers the one shared zone list, and saves only when
 *     Save is pressed, then says "Saved".
 *   · **Alerts are one "coming soon" line**, not a page of switches that looked live. What does
 *     reach an owner today — the banner on every page when credit runs out or a card is declined —
 *     is said plainly.
 *   · **One API-keys section.** The second "API key · Copy · Regenerate key" block is gone.
 *   · **Controls follow the role**: renaming (Owners and Admins), API keys (Owners and Admins),
 *     deleting the workspace (Owners) are offered only to those roles, and others read who can.
 *
 * AMENDED 2026-09-25, merged with the Settings pass of customer-portal review items 184–196:
 *
 *   · **Enter submits Save** (and nothing else saves: not a change, not a blur). A blank name is
 *     never sent: leaving the field blank, or saving it blank, puts the saved name back and says
 *     so (item 185). The name is at most 60 characters, counted as the API counts them, with the
 *     same counter as New workspace and Rename (one rule for create and rename, review #153).
 *   · **A time-zone change asks first** when Save is pressed, naming what moves (item 184);
 *     leaving the page with an unsaved change asks first too (`beforeunload`).
 *   · **The billing email is the billing account's** (`GET /v1/billing/balance`'s
 *     `billing_email`), the same for every member, not the signed-in person's (item 186).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Input, Select, Tag } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { ApiKeyPanel } from "./api-key-panel.js";
import { PermissionMatrix } from "./permission-matrix.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authSessionQuery, creditBalanceQuery, workspaceDetailQuery } from "../../api/query.js";
import { useCurrentWorkspace } from "../../shell/current-workspace.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { zoneChoice } from "../workspaces/offered-zone.js";
import { WORKSPACE_NAME_MAXIMUM, nameLengthOf } from "../workspaces/workspace-name.js";
import type { WorkspaceDetailView } from "../../api/gateway.js";

import "./settings.css";
import "../billing/billing.css";

export function SettingsScreen(): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const authSession = useQuery(authSessionQuery(gateway));
  const workspaceId = authSession.data?.workspaceId ?? "";
  const detail = useQuery({
    ...workspaceDetailQuery(gateway, workspaceId),
    enabled: workspaceId !== "",
  });
  // The address invoices go to is the billing account's, the same for everyone (review item 186).
  const balance = useQuery(creditBalanceQuery(gateway));
  // The name is the workspace listing's, not the sign-in's (review #150).
  const currentName = useCurrentWorkspace().workspace?.name ?? null;

  if (authSession.data === undefined || authSession.data === null) {
    return <p className="c-note">{portalText("core", "settings.reading_your_workspace_28eb57")}</p>;
  }

  return (
    <section className="c-page c-settings">
      <h1>{portalText("core", "settings.settings_c7f73b")}</h1>

      <div className="c-settings-column">
        {/* --- the workspace itself ------------------------------------------------------ */}
        {detail.data === undefined ? (
          detail.isError ? (
            <p className="c-note c-note-critical">{portalText("core", "settings.this_workspace_s_name_and_time_zone_could_not_be_read_so_they_are_445c5d")}{" "}
              <Button
                variant="ghost"
                onClick={() => {
                  void detail.refetch();
                }}
              >{portalText("core", "workspaces.try_again_042c86")}</Button>
            </p>
          ) : (
            <p className="c-note">{portalText("core", "settings.reading_this_workspace_s_name_and_time_zone_dfa56e")}</p>
          )
        ) : (
          <WorkspaceForm workspace={detail.data} />
        )}

        <ApiKeyPanel workspaceId={workspaceId} accountId={authSession.data.accountId} />

        <PermissionMatrix />

        {/* --- alerts -------------------------------------------------------------------- */}
        <div>
          <p className="c-settings-title">{portalText("core", "settings.alerts_d0efe0")}{" "}<Tag tone="neutral">{portalText("core", "settings.soon_32d3b2")}</Tag>
          </p>
          <p className="c-settings-dek">{portalText("core", "settings.choosing_alerts_by_email_sms_or_slack_is_coming_soon_and_none_are_eb4bd3")}</p>
        </div>

        <Field label={portalText("core", "settings.billing_email_5e0c4e")}>
          {(control) => (
            <Input
              {...control}
              type="email"
              value={balance.data === undefined ? "" : balance.data.billingEmail}
              placeholder={balance.data === undefined ? portalText("core", "settings.reading_the_billing_account_96021b") : undefined}
              disabled
              readOnly
              title={portalText("core", "settings.invoices_for_the_whole_account_are_sent_here_whoever_is_signed_in_3685e3")}
            />
          )}
        </Field>
      </div>

      {/* Deleting asks for the name to be typed, so it waits for the real one. */}
      {currentName === null ? null : <DangerZone workspaceId={workspaceId} workspaceName={currentName} />}
    </section>
  );
}

/**
 * The workspace's name and time zone, saved together with an explicit Save.
 *
 * Mounted only once the workspace has been read, so the fields start from the stored values —
 * never from a default. "Saved" shows once the re-read workspace equals what was typed. Enter in
 * the name submits Save; a changed time zone is confirmed first, because it moves every agent's
 * opening hours and "today" (review items 184, 61).
 */
function WorkspaceForm({ workspace }: { readonly workspace: WorkspaceDetailView }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const member = useMemberCan();
  const [name, setName] = useState(workspace.name);
  const [timeZone, setTimeZone] = useState(workspace.timeZone);
  const [message, setMessage] = usePortalMessage();
  const [saved, setSaved] = useState(false);
  // A time-zone change waits here, after Save, until it is confirmed.
  const [confirmingZone, setConfirmingZone] = useState<string | null>(null);

  const refusal = member.whyNot("workspace.rename");
  const changed = name.trim() !== workspace.name || timeZone !== workspace.timeZone;
  const nameLength = nameLengthOf(name);
  const nameTooLong = nameLength > WORKSPACE_NAME_MAXIMUM;

  // A change typed and not yet saved would be lost by a reload or a closed tab: ask first
  // (review item 184).
  useEffect(() => {
    if (!changed) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
    };
  }, [changed]);

  const save = useMutation({
    mutationFn: () =>
      gateway.renameWorkspace(workspace.workspaceId, { name: name.trim(), timeZone }),
    onSuccess: async () => {
      setMessage(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
    },
    onError: (error: unknown) => {
      setSaved(false);
      setMessage(() => (apiProblemText(error, portalText("core", "settings.that_change_could_not_be_saved_76212e"))));
    },
  });

  /** A blank name is never sent: the saved one comes back, and the page says so (item 185). */
  const restoreBlankName = (): boolean => {
    if (name.trim() !== "") return false;
    setName(workspace.name);
    setSaved(false);
    setMessage(() => (portalText("core", "settings.a_workspace_needs_a_name_so_it_is_still_called_a76595", { value0: workspace.name })));
    return true;
  };

  /** Save: a blank name is put back, a changed zone asks first, anything else is sent. */
  const submit = (): void => {
    if (refusal !== null || save.isPending) return;
    if (restoreBlankName()) return;
    if (!changed || nameTooLong) return;
    if (timeZone !== workspace.timeZone) {
      setConfirmingZone(timeZone);
      return;
    }
    save.mutate();
  };

  return (
    <>
      <form
        className="c-settings-workspace"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="c-settings-pair">
          <Field
            label={portalText("core", "workspaces.workspace_name_6e0f2b")}
            description={portalText("core", "workspaces._of_characters_381b09", { value0: String(nameLength), value1: String(WORKSPACE_NAME_MAXIMUM) })}
            {...(nameTooLong
              ? { error: portalText("core", "workspaces.a_workspace_name_can_be_at_most_characters_a6bb31", { value0: String(WORKSPACE_NAME_MAXIMUM) }) }
              : {})}
          >
            {(control) => (
              <Input
                {...control}
                value={name}
                disabled={refusal !== null}
                onChange={(event) => {
                  setSaved(false);
                  setMessage(null);
                  setName(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  // Enter submits Save — and only Save: leaving the field saves nothing.
                  if (event.key === portalText("core", "settings.enter_2b9ece")) {
                    event.preventDefault();
                    submit();
                  }
                }}
                onBlur={() => {
                  restoreBlankName();
                }}
              />
            )}
          </Field>

          <Field label={portalText("core", "auth.time_zone_eea79a")}>
            {(control) => (
              <Select
                {...control}
                value={timeZone}
                disabled={refusal !== null}
                onChange={(event) => {
                  setSaved(false);
                  setTimeZone(event.currentTarget.value);
                }}
              >
                {zoneChoice(workspace.timeZone).map((one) => (
                  <option key={one} value={one}>
                    {one}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <div className="c-settings-actions">
          {refusal === null ? (
            <Button
              variant="secondary"
              type="submit"
              disabled={!changed || nameLength === 0 || nameTooLong || save.isPending}
            >{portalText("core", "account_admin.save_efc007")}</Button>
          ) : (
            <p className="c-note">{refusal}</p>
          )}
          {saved && !changed ? (
            <span className="c-note" role="status">{portalText("core", "account_admin.saved_9d4af6")}</span>
          ) : null}
        </div>
        <p className="c-settings-dek">{portalText("core", "settings.opening_hours_today_and_the_billing_day_are_all_taken_in_this_tim_8f1fea")}</p>
        {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
      </form>

      <Dialog
        open={confirmingZone !== null}
        title={portalText("core", "settings.change_the_time_zone_2b4dcc")}
        onClose={() => {
          setConfirmingZone(null);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                // A cancelled change saves nothing, and the box shows the stored zone again.
                setConfirmingZone(null);
                setTimeZone(workspace.timeZone);
              }}
            >{portalText("core", "workspaces.cancel_77dfd2")}</Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmingZone(null);
                save.mutate();
              }}
            >
              {confirmingZone === null ? portalText("core", "settings.change_it_0b2cad") : portalText("core", "settings.use_239721", { value0: confirmingZone })}
            </Button>
          </>
        }
      >
        <p className="c-note">{portalText("core", "settings.every_agent_in_095c77")}{" "}{workspace.name}{" "}{portalText("core", "settings.will_keep_its_opening_hours_in_84e7fd")}{" "}{confirmingZone}{portalText("core", "settings._and_today_on_the_dashboard_and_in_usage_will_start_at_midnight_t_697c25")}</p>
      </Dialog>
    </>
  );
}

/**
 * Deleting a workspace.
 *
 * The confirmation asks for the workspace's name rather than for a click, because the act is
 * irreversible from the console's side and a misclick on a list of three similarly-named
 * workspaces is exactly how the wrong one goes. The server archives rather than deletes — the
 * conversations, the ledger and the audit entries all point at the row — and refuses the
 * account's last workspace outright. Owners only; everyone else reads who can.
 */
function DangerZone({
  workspaceId,
  workspaceName,
}: {
  readonly workspaceId: string;
  readonly workspaceName: string;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const member = useMemberCan();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [message, setMessage] = usePortalMessage();

  const archive = useMutation({
    mutationFn: () => gateway.archiveWorkspace(workspaceId),
    onSuccess: async () => {
      setOpen(false);
      setTyped("");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
    },
    onError: (error: unknown) => {
      setMessage(() => (apiProblemText(error, portalText("core", "workspaces.that_workspace_could_not_be_deleted_4481b5"))));
    },
  });

  const refusal = member.whyNot("workspace.delete");

  return (
    <div className="c-danger">
      <p className="c-danger-kicker">{portalText("core", "settings.danger_zone_963a65")}</p>
      <p className="c-danger-note">{portalText("core", "settings.deleting_the_workspace_stops_every_agent_immediately_and_cannot_b_986529")}</p>
      {refusal === null ? (
        <Button
          variant="secondary"
          onClick={() => {
            setOpen(true);
          }}
        >{portalText("core", "workspaces.delete_workspace_bc2cf9")}</Button>
      ) : (
        <p className="c-note">{refusal}</p>
      )}

      <Dialog
        open={open}
        title={portalText("core", "settings.delete_this_workspace_b13c05")}
        onClose={() => {
          setOpen(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
              }}
            >{portalText("core", "workspaces.cancel_77dfd2")}</Button>
            <Button
              variant="primary"
              disabled={typed !== workspaceName || archive.isPending}
              onClick={() => {
                archive.mutate();
              }}
            >{portalText("core", "settings.delete_it_328190")}</Button>
          </>
        }
      >
        <p className="c-note">{portalText("core", "settings.every_agent_in_095c77")}{" "}{workspaceName}{" "}{portalText("core", "settings.stops_answering_the_moment_this_is_done_f62b51")}</p>
        <Field label={portalText("core", "settings.type_to_confirm_097272", { value0: workspaceName })}>
          {(control) => (
            <Input
              {...control}
              value={typed}
              onChange={(event) => {
                setTyped(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
      </Dialog>
    </div>
  );
}
